import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Tippy from '@tippyjs/react'
import withTheme from '../styles/withTheme'

import './rf-tooltip.scss'

const Tooltip = forwardRef(function Tooltip(props, ref) {
	const {
		children,
		className,
		content,
		delay = 100,
		disabled = false,
		interactive = false,
		maxWidth = 350,
		maxHeight,
		onClickOutSide,
		offsetX = 0,
		offsetY = 25,
		placement = 'top',
		theme,
		trigger = 'mouseenter focus',
		visible,
		...other
	} = props

	const handleClickOutside = (instance, event) => {
		typeof onClickOutSide === 'function' && onClickOutSide(instance, event)
	}

	return (
		<Tippy
			animation="shift-toward"
			className={clsx('rf-tooltip', className)}
			content={
				<div
					className="tippy-wrapper"
					style={{
						maxHeight: maxHeight ? `${maxHeight}px` : 'none'
					}}
				>
					{content}
				</div>
			}
			delay={delay}
			disabled={disabled}
			interactive={interactive}
			maxWidth={maxWidth}
			offset={[offsetX, offsetY]}
			onClickOutside={handleClickOutside}
			placement={placement}
			ref={ref}
			theme={`rf-tooltip--theme-${theme}`}
			trigger={visible === undefined ? trigger : undefined}
			visible={visible}
			onMount={instance => {
				instance.reference.setAttribute('aria-describedby', `tippy-${instance.id}`)
			}}
			onHide={instance => {
				instance.reference.removeAttribute('aria-describedby')
			}}
			{...other}
		>
			{children}
		</Tippy>
	)
})

export default withTheme('rf-tooltip')(Tooltip)

Tooltip.defaultProps = {
	delay: 100,
	disabled: false,
	interactive: false,
	maxWidth: 350,
	offsetX: 0,
	offsetY: 25,
	placement: 'bottom',
	trigger: 'mouseenter focus',
	visible: undefined
}

Tooltip.propTypes = {
	/**
	 * The trigger component of the tooltip.
	 */
	children: PropTypes.node,

	/**
	 * @ignore
	 */
	className: PropTypes.string,

	/**
	 * The content of the tooltip.
	 */
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

	/**
	 * Delay in ms once a trigger event is fired before a tippy shows or hides.
	 * @default 100
	 */
	delay: PropTypes.number,

	/**
	 * If `true`, the tooltip is disabled.
	 * @default false
	 */
	disabled: PropTypes.bool,

	/**
	 * Determines if the tippy has interactive content inside of it,
	 * so that it can be hovered over and clicked inside without hiding.
	 * @default false
	 */
	interactive: PropTypes.bool,

	/**
	 * Specifies the maximum width, in px, of the tooltip. `none` disables maxWidth
	 * @default 350
	 */
	maxWidth: PropTypes.oneOfType([PropTypes.oneOf(['none']), PropTypes.number]),

	/**
	 * Specifies the maximum height, in px, of the tooltip.
	 */
	maxHeight: PropTypes.number,

	/**
	 * Displaces the tooltip from its reference element in pixels on the X axis.
	 * @default 0
	 */
	offsetX: PropTypes.number,

	/**
	 * Displaces the tooltip from its reference element in pixels on the Y axis.
	 * @default 25
	 */
	offsetY: PropTypes.number,

	/**
	 * The preferred placement of the tippy.
	 * @default 'bottom'
	 */
	placement: PropTypes.oneOf([
		'top',
		'top-start',
		'top-end',
		'right',
		'right-start',
		'right-end',
		'bottom',
		'bottom-start',
		'bottom-end',
		'left',
		'left-start',
		'left-end'
	]),

	/**
	 * Determines the events that cause the tippy to show. Multiple event names are separated by spaces.
	 * @default 'mouseenter focus'
	 */
	trigger: PropTypes.string,

	/**
	 * If `true` or `false`, the tooltip visibility is controlled.
	 * @default undefined
	 */
	visible: PropTypes.bool
}
